import { Injectable } from '@angular/core';
import * as joint from '@clientio/rappid';
import { BehaviorSubject } from 'rxjs';

import { FlowchartConfig } from '../base/flowchart.config';
import { HeaderElement } from '../shapes/shared-header';

@Injectable({
  providedIn: 'root',
})
export class SizeService {
  public updatePaperHeight: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  setNodeSize(titleSize: number): { width: number; height: number } {
    if (titleSize < 25) {
      return FlowchartConfig.nodeSize.s;
    } else if (titleSize > 25 && titleSize < 35) {
      return FlowchartConfig.nodeSize.m;
    } else {
      return FlowchartConfig.nodeSize.l;
    }
  }

  createResizingTool(_cellView: joint.dia.CellView) {
    return new joint.ui.FreeTransform({
      cellView: _cellView,
      minWidth: 0,
      minHeight: 0,
      maxHeight: Infinity,
      resizeGrid: { width: FlowchartConfig.paper.gridSize * 2, height: FlowchartConfig.paper.gridSize * 2 },
      resizeDirections: ['top-left', 'top-right', 'bottom-left', 'bottom-right'],
      allowRotation: false,
      theme: 'material',
    });
  }

  resizeParentToFit(parentElement: joint.dia.Element, graph: joint.dia.Graph) {
    const subflowDynamicElementIds = this.getSubflowDynamicElementIds(parentElement, graph);
    let subflowElementCount = 0;
    if (subflowDynamicElementIds) {
      subflowElementCount = subflowDynamicElementIds.length;
    }

    const padding = FlowchartConfig.paper.gridSize * 2;

    if (subflowElementCount === 1) {
      this.moveSubflowWhenSubElementIsSingle(parentElement, graph, padding, subflowDynamicElementIds);
    } else if (subflowElementCount > 1) {
      this.resizeSubflowOnMultipleSubElements(parentElement, graph, padding);
    }

    const header = parentElement.getEmbeddedCells()[0];
    (header as HeaderElement).resizeShape();
  }

  getSizeToGrid(size: joint.dia.Size): joint.dia.Size {
    let { width, height } = size;
    const gridSize = FlowchartConfig.paper.gridSize * 2;
    if (width % gridSize !== 0) {
      width = Math.ceil(width / gridSize) * gridSize;
    }

    if (height % gridSize !== 0) {
      height = Math.ceil(height / gridSize) * gridSize;
    }

    return { width, height };
  }

  public getEmbedsBoundingBox(parentElement: joint.dia.Element): joint.dia.BBox {
    let minX = Infinity,
      minY = Infinity,
      maxX = -Infinity,
      maxY = -Infinity;

    const embeddedCells = parentElement.getEmbeddedCells().filter((cell) => !cell.attributes.isElementTag && !cell.attributes.isHeader);

    for (let i = 0; i < embeddedCells.length; i++) {
      const cell = embeddedCells[i];
      const bbox = cell.getBBox();

      minX = Math.min(minX, bbox.x);
      minY = Math.min(minY, bbox.y);
      maxX = Math.max(maxX, bbox.x + bbox.width);
      maxY = Math.max(maxY, bbox.y + bbox.height);
    }

    return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
  }

  private getSubflowDynamicElementIds(subflow: joint.dia.Element, graph: joint.dia.Graph) {
    const subflowElementIds = (subflow.attributes.embeds as string[]).filter((embed: string) => {
      const subElement = graph.getCell(embed);
      return !(subElement.attributes.isElementTag || subElement.attributes.isHeader);
    });

    return subflowElementIds;
  }

  private calculateTotalTagWidth(subflow: joint.dia.Element): number {
    const tags = subflow.getEmbeddedCells().filter((e) => e.attributes.isElementTag);
    let tagWidth = 0;
    tags.forEach((tag) => {
      tagWidth += (tag as joint.dia.Element).attributes.size.width + FlowchartConfig.tag.padding.paddingBetweenTags;
    });

    return tagWidth;
  }

  private moveSubflowWhenSubElementIsSingle(
    parentElement: joint.dia.Element,
    graph: joint.dia.Graph,
    padding: number,
    subflowDynamicElementIds: string[],
  ) {
    const minSize = FlowchartConfig.containerNode.minSize;
    const tagWidth = this.calculateTotalTagWidth(parentElement);
    const onlyElement: joint.dia.Element = graph.getCell(subflowDynamicElementIds[0]) as joint.dia.Element;
    const minWidth: number = tagWidth >= onlyElement.attributes.size.width ? tagWidth : onlyElement.attributes.size.width;
    const height = Math.ceil(onlyElement.attributes.size.height / padding) * padding + 6 * padding;
    const width = minWidth + 2 * padding;
    parentElement.resize(width > minSize.width ? width : minSize.width, height > minSize.height ? height : minSize.height);
    parentElement.position(
      Math.ceil(
        (onlyElement.attributes.position.x - (parentElement.attributes.size.width - onlyElement.attributes.size.width) / 2) / (padding / 2),
      ) *
        (padding / 2),
      Math.ceil((onlyElement.attributes.position.y - 4 * padding) / (padding / 2)) * (padding / 2),
    );
  }

  private resizeSubflowOnMultipleSubElements(parentElement: joint.dia.Element, graph: joint.dia.Graph, padding: number) {
    const bbox = this.calculateBboxWithLinks(parentElement, graph);
    const width = Math.ceil((bbox.width + padding) / padding) * padding + padding * 3;
    const height = Math.ceil((bbox.height + 2 * padding) / padding) * padding + padding;

    parentElement.resize(width, height + FlowchartConfig.paper.gridSize * 2);
    parentElement.position(
      Math.ceil((bbox.x - 2 * padding) / (padding / 2)) * (padding / 2),
      Math.ceil((bbox.y - 2 * padding) / (padding / 2)) * (padding / 2) - FlowchartConfig.paper.gridSize * 2,
    );

    const parentBbox = parentElement.getBBox();

    const tags = parentElement.getEmbeddedCells().filter((e) => e.attributes.isElementTag) as joint.dia.Element[];
    tags.forEach((tag) => {
      tag.position(tag.attributes.position.x, parentBbox.y + 24);
    });
  }

  private calculateBboxWithLinks(parentElement: joint.dia.Element, graph: joint.dia.Graph) {
    const parentBbox = this.getEmbedsBoundingBox(parentElement);
    const embeds = parentElement.getEmbeddedCells();
    let maxX = parentBbox.x;
    let maxY = parentBbox.y;
    embeds.forEach((el) => {
      graph.getConnectedLinks(el).forEach((link) => {
        link.attributes.vertices?.forEach((vertex) => {
          if (vertex.x > maxX) {
            maxX = vertex.x;
          }

          if (vertex.y > maxY) {
            maxY = vertex.y;
          }
        });
      });
    });

    if (maxX > parentBbox.x + parentBbox.width) {
      parentBbox.width = maxX - parentBbox.x;
    }

    if (maxY > parentBbox.y + parentBbox.height) {
      parentBbox.height = maxY - parentBbox.y;
    }

    return parentBbox;
  }
}
